import React, { useState } from "react";
import { Modal } from "../common/Modal";
import { DraftSection, ProposalSectionGroup, RequirementCategory } from "../../types";
import { useStaffDirectory } from "../../services/staffService";
import { Loader2 } from "lucide-react";

export interface EditSectionFormData {
  title: string;
  sectionRef: string;
  category: RequirementCategory;
  assignedTo?: string;
  assignedToEmail?: string;
  reviewerName?: string;
  reviewerEmail?: string;
  targetWordCount?: number;
  dueDate?: string;
  notes?: string;
}

interface EditSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  section?: ProposalSectionGroup | DraftSection | null;
  onSave: (data: EditSectionFormData) => Promise<void>;
}

export const EditSectionModal: React.FC<EditSectionModalProps> = ({
  isOpen,
  onClose,
  section,
  onSave,
}) => {
  const { staff } = useStaffDirectory({ rfpOnly: true });

  const [title, setTitle] = useState(section?.title || "");
  const [sectionRef, setSectionRef] = useState(section?.sectionRef || "");
  const [category, setCategory] = useState<RequirementCategory>(section?.category || "technical");
  const [assignedEmail, setAssignedEmail] = useState(section?.assignedToEmail || "");
  const [reviewerEmail, setReviewerEmail] = useState(section?.reviewerEmail || "");
  const [targetWordCount, setTargetWordCount] = useState<string>(
    section?.targetWordCount ? String(section.targetWordCount) : "750",
  );
  const [dueDate, setDueDate] = useState(section?.dueDate || "");
  const [notes, setNotes] = useState(section?.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Section title is required.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const assignedStaff = staff.find(
        (s) => s.email.toLowerCase() === assignedEmail.toLowerCase(),
      );
      const reviewerStaff = staff.find(
        (s) => s.email.toLowerCase() === reviewerEmail.toLowerCase(),
      );

      const parsedTargetWords = parseInt(targetWordCount, 10);

      await onSave({
        title: title.trim(),
        sectionRef: sectionRef.trim() || (undefined as unknown as string),
        category,
        assignedTo:
          assignedStaff?.displayName || (assignedEmail ? assignedEmail.split("@")[0] : undefined),
        assignedToEmail: assignedEmail.trim() || undefined,
        reviewerName:
          reviewerStaff?.displayName || (reviewerEmail ? reviewerEmail.split("@")[0] : undefined),
        reviewerEmail: reviewerEmail.trim() || undefined,
        targetWordCount: Number.isNaN(parsedTargetWords) ? undefined : parsedTargetWords,
        dueDate: dueDate.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (err) {
      console.error("Failed to save section:", err);
      setError(err instanceof Error ? err.message : "Failed to save section.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={section ? "Edit Proposal Section" : "Add Proposal Section"}
    >
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-rose-50 p-2.5 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Section Title <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Technical Approach & Workflow"
              required
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Outline / Section Ref
            </label>
            <input
              type="text"
              value={sectionRef}
              onChange={(e) => setSectionRef(e.target.value)}
              placeholder="e.g. SEC 3.2"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as RequirementCategory)}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              <option value="technical">Technical</option>
              <option value="management">Management & Staffing</option>
              <option value="qualifications">Past Performance & Qualifications</option>
              <option value="pricing">Pricing & Cost</option>
              <option value="compliance">Compliance & Security</option>
              <option value="general">General</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Target Word Count
            </label>
            <input
              type="number"
              min="0"
              step="50"
              value={targetWordCount}
              onChange={(e) => setTargetWordCount(e.target.value)}
              placeholder="e.g. 1000"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Section Lead / Drafter
            </label>
            <select
              value={assignedEmail}
              onChange={(e) => setAssignedEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              <option value="">Unassigned</option>
              {staff.map((s) => (
                <option key={s.id} value={s.email}>
                  {s.displayName} ({s.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              SME Reviewer
            </label>
            <select
              value={reviewerEmail}
              onChange={(e) => setReviewerEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              <option value="">No Reviewer Assigned</option>
              {staff.map((s) => (
                <option key={s.id} value={s.email}>
                  {s.displayName} ({s.email})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
            Due Date (Target Draft Completion)
          </label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
            Section Strategy & Win Themes
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Highlight key win themes, differentiators, case studies, or proof points to include in this section..."
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="btn-animate rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97] disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <span>{section ? "Save Changes" : "Create Section"}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
